import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Car, CalendarCheck, Fuel, AlertTriangle, Gauge, Route, Download, RefreshCw, CheckCircle2, Clock, ArrowRight, Building2, MapPin, Users, CalendarDays, Wrench, Droplets } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine, LabelList } from "recharts";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import FleetMap from "../components/FleetMap";
import { fmtDate, fmtDateTime } from "../lib/format";

const BK_STATUS = {
  menunggu: { label: "Menunggu Kelulusan", cls: "bg-slate-500 hover:bg-slate-500" },
  menunggu_kelulusan: { label: "Menunggu Kelulusan Admin", cls: "bg-amber-500 hover:bg-amber-500" },
  diluluskan: { label: "Diluluskan", cls: "bg-[#198754] hover:bg-[#198754]" },
  ditolak: { label: "Ditolak", cls: "bg-[#DC3545] hover:bg-[#DC3545]" },
  selesai: { label: "Selesai", cls: "bg-[#10056E] hover:bg-[#10056E]" },
};

function LatestBookingCard({ booking }) {
  const running = booking.trip_status === "berjalan";
  const canTrip = booking.status === "diluluskan" && !booking.trip_logged;
  return (
    <Card className="border-slate-200 shadow-sm rounded-xl border-l-4 border-l-[#10056E]" data-testid="latest-booking-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground font-medium">Tempahan Terkini Anda</p>
            <p className="text-lg font-bold mt-1 truncate" data-testid="latest-booking-destination">{booking.destination}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{fmtDateTime(booking.start_datetime)} → {fmtDateTime(booking.end_datetime)}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {booking.vehicle_reg ? `Kenderaan: ${booking.vehicle_reg}` : "Kenderaan belum ditugaskan"} · {booking.estimated_distance} km
            </p>
          </div>
          <Badge className={running ? "bg-sky-600 hover:bg-sky-600" : BK_STATUS[booking.status]?.cls} data-testid="latest-booking-status">
            {running ? "Sedang Berjalan" : BK_STATUS[booking.status]?.label}
          </Badge>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          {canTrip && (
            <Link to={`/perjalanan/${booking.id}`} className="flex-1">
              <Button className={`w-full h-11 ${running ? "bg-[#DC3545] hover:bg-[#bb2d3b]" : "bg-[#198754] hover:bg-[#157347]"}`} data-testid="latest-booking-trip-btn">
                {running ? "Tamat Perjalanan" : "Mula Perjalanan"} <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
          )}
          <Link to={`/tempahan/${booking.id}`} className="flex-1">
            <Button variant="outline" className="w-full h-11 border-[#10056E] text-[#10056E] hover:bg-indigo-50" data-testid="latest-booking-detail-btn">
              Lihat Butiran
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

const TONES = {
  indigo: "bg-indigo-50 text-[#10056E]",
  blue: "bg-sky-50 text-sky-600",
  green: "bg-emerald-50 text-emerald-600",
  amber: "bg-sky-50 text-sky-700",
  red: "bg-red-50 text-[#DC3545]",
  purple: "bg-purple-50 text-purple-600",
  pink: "bg-pink-50 text-pink-600",
  teal: "bg-teal-50 text-teal-600",
};

function StatCard({ icon: Icon, label, value, sub, testId, tone = "indigo" }) {
  return (
    <Card className="stat-card border-slate-200 shadow-sm rounded-xl h-full" data-testid={testId}>
      <CardContent className="p-4 flex items-start gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${TONES[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground font-medium truncate">{label}</p>
          <p className="text-xl font-bold tracking-tight mt-0.5">{value}</p>
          {sub && <p className="text-[11px] text-slate-500 truncate">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function AlertBanner({ tone, icon: Icon, text, linkTo, linkLabel, testId }) {
  const cls = tone === "red"
    ? "bg-red-50 border-red-200 text-red-800"
    : "bg-indigo-50 border-indigo-200 text-[#10056E]";
  return (
    <div className={`flex items-center gap-3 border rounded-lg px-4 py-3 text-sm ${cls}`} data-testid={testId}>
      <Icon className="h-4 w-4 shrink-0" />
      <span className="font-semibold">{text}</span>
      <Link to={linkTo} className="ml-auto flex items-center gap-1 font-semibold underline-offset-2 hover:underline whitespace-nowrap">
        {linkLabel} <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}

function UtilChip({ label, value, sub, tone = "text-[#10056E]" }) {
  return (
    <div className="border border-slate-200 bg-white rounded-lg px-3 py-2 min-w-32">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">{label}</p>
      <p className={`text-lg font-bold leading-tight ${tone}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-500">{sub}</p>}
    </div>
  );
}

function UtilChart({ title, desc, items, avg, idleText, idleBadges, testId }) {
  const maxVal = Math.max(avg || 0, ...(items || []).map((i) => i.tugasan), 2);
  return (
    <Card className="border-slate-200 shadow-sm rounded-xl" data-testid={testId}>
      <CardHeader>
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
        <CardDescription className="text-xs">{desc} · purata {avg} tugasan — <span className="text-[#DC3545] font-semibold">merah = melebihi purata (terlebih guna)</span></CardDescription>
      </CardHeader>
      <CardContent>
        {items?.length ? (
          <ResponsiveContainer width="100%" height={Math.max(200, items.length * 38)}>
            <BarChart data={items} layout="vertical" margin={{ left: 8, right: 64 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" allowDecimals={false} domain={[0, Math.ceil(maxVal) + 1]} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="nama" width={150} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, n, p) => [`${v} tugasan · ${p.payload.km} km`, p.payload.nama]} />
              <ReferenceLine x={avg} stroke="#DC3545" strokeDasharray="4 4" />
              <Bar dataKey="tugasan" radius={[0, 4, 4, 0]} barSize={20}>
                <LabelList dataKey="km" position="right" formatter={(v) => `${v} km`} style={{ fontSize: 10, fill: "#64748b" }} />
                {items.map((it, i) => (
                  <Cell key={i} fill={it.tugasan > avg ? "#DC3545" : "#10056E"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-muted-foreground py-10 text-center">Tiada tugasan dalam 30 hari.</p>
        )}
        {idleText && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-[11px] text-muted-foreground">{idleText}</p>
            {idleBadges?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {idleBadges.map((b) => <Badge key={b} variant="secondary" className="text-[10px]">{b}</Badge>)}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PemanduDashboard({ data, tenant, load, refreshing }) {
  const s = data.stats;
  return (
    <div className="space-y-6 fade-up" data-testid="pemandu-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard Pemandu</h1>
          <p className="text-sm text-muted-foreground mt-1">Tugasan pemanduan anda — {tenant?.name}</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="dashboard-refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Muat Semula
        </Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Clock} label="Akan Datang" value={s.tugasan_akan_datang} testId="stat-pemandu-upcoming" tone="amber" />
        <StatCard icon={CheckCircle2} label="Selesai" value={s.tugasan_selesai} testId="stat-pemandu-done" tone="green" />
        <StatCard icon={CalendarCheck} label="Jumlah Tugasan" value={s.jumlah_tugasan} testId="stat-pemandu-total" tone="indigo" />
        <StatCard icon={Route} label="Jarak (km)" value={s.jumlah_km} testId="stat-pemandu-km" tone="blue" />
      </div>
      <Card className="border-slate-200 shadow-sm rounded-xl">
        <CardHeader>
          <CardTitle className="text-base font-semibold">Tugasan Saya</CardTitle>
          <CardDescription className="text-xs">Tempahan yang anda ditugaskan sebagai pemandu</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3" data-testid="pemandu-assignments-list">
          {(!data.assignments || data.assignments.length === 0) && <p className="text-sm text-muted-foreground">Tiada tugasan lagi.</p>}
          {data.assignments?.map((b) => (
            <Link key={b.id} to={`/tempahan/${b.id}`} className="flex flex-wrap items-center justify-between gap-2 border-b last:border-0 pb-3 last:pb-0 hover:bg-slate-50 rounded-md px-2 -mx-2 py-1.5 transition-colors">
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{b.destination} · <span className="text-[#10056E]">{b.vehicle_reg || "-"}</span></p>
                <p className="text-xs text-muted-foreground">{fmtDateTime(b.start_datetime)} → {fmtDateTime(b.end_datetime)} · pemohon: {b.driver_name}</p>
              </div>
              <Badge className={BK_STATUS[b.status]?.cls}>{BK_STATUS[b.status]?.label}</Badge>
            </Link>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

async function downloadCsv(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Dashboard() {
  const { user, tenant } = useAuth();
  const [data, setData] = useState(null);
  const [positions, setPositions] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/dashboard");
      setData(r.data);
      if (r.data.role !== "driver" && r.data.role !== "pemandu") {
        const p = await api.get("/telematics/positions");
        setPositions(p.data.positions);
      }
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-sm text-muted-foreground">Memuatkan...</p>;

  if (data.role === "pemandu") return <PemanduDashboard data={data} tenant={tenant} load={load} refreshing={refreshing} />;

  const s = data.stats;
  const isDriver = data.role === "driver";

  return (
    <div className="space-y-6 fade-up" data-testid={`${data.role}-dashboard`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isDriver ? "Ringkasan tempahan dan perjalanan anda" : "Operasi · fleet · penggunaan sepintas lalu"} — {tenant?.name}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="dashboard-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Muat Semula
          </Button>
          {!isDriver && (
            <>
              <Button variant="outline" size="sm" onClick={() => downloadCsv("/reports/trips", "laporan_log_perjalanan.csv")} data-testid="export-trips-btn">
                <Download className="h-4 w-4 mr-2" /> Eksport Log (CSV)
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadCsv("/reports/bookings", "laporan_tempahan.csv")} data-testid="export-bookings-btn">
                <Download className="h-4 w-4 mr-2" /> Eksport Tempahan (CSV)
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Staf: latest booking status */}
      {isDriver && data.latest_booking && <LatestBookingCard booking={data.latest_booking} />}

      {/* Admin: final approval queue */}
      {user?.role === "admin" && data.approval_bookings?.length > 0 && (
        <Card className="border-amber-300 shadow-sm rounded-xl border-l-4 border-l-amber-500 bg-amber-50/50" data-testid="final-approval-queue">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-amber-600" /> Kelulusan Akhir Diperlukan
            </CardTitle>
            <CardDescription className="text-xs mt-0.5">{data.approval_bookings.length} tempahan telah ditugaskan kenderaan dan menunggu kelulusan akhir anda.</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="divide-y divide-amber-200/60">
              {data.approval_bookings.slice(0, 5).map((b) => (
                <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`approval-booking-${b.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{b.driver_name} → {b.destination} · <span className="text-[#10056E]">{b.vehicle_reg}</span></p>
                    <p className="text-xs text-muted-foreground">{fmtDateTime(b.start_datetime)} · ditugaskan oleh {b.assigned_by}</p>
                  </div>
                  <Link to={`/tempahan/${b.id}`}>
                    <Button size="sm" className="h-8 text-xs bg-[#198754] hover:bg-[#157347]" data-testid={`review-approval-${b.id}`}>
                      Semak & Lulus <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </Link>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main action: agency info + pending bookings */}
      {!isDriver && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          <Card className="border-slate-200 shadow-sm rounded-xl lg:col-span-1" data-testid="agency-info-card">
            <CardContent className="p-5 flex flex-col items-start gap-3 h-full">
              {tenant?.settings?.agency_logo ? (
                <img src={tenant.settings.agency_logo} alt="Logo Agensi" className="h-28 w-28 object-contain" data-testid="agency-logo-dashboard" />
              ) : (
                <div className="h-16 w-16 rounded-lg border bg-slate-50 flex items-center justify-center">
                  <Building2 className="h-7 w-7 text-slate-300" />
                </div>
              )}
              <div>
                <p className="text-base font-bold leading-snug" data-testid="agency-name-dashboard">{tenant?.name}</p>
                {tenant?.settings?.agency_code && <p className="text-xs text-muted-foreground mt-0.5">Kod Agensi: {tenant.settings.agency_code}</p>}
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                {tenant?.settings?.agency_address && <p>{tenant.settings.agency_address}{tenant.settings.agency_postcode ? `, ${tenant.settings.agency_postcode}` : ""}{tenant.settings.agency_city ? ` ${tenant.settings.agency_city}` : ""}{tenant.settings.agency_state ? `, ${tenant.settings.agency_state}` : ""}</p>}
                {tenant?.settings?.agency_phone && <p>Tel: {tenant.settings.agency_phone}</p>}
                {tenant?.settings?.agency_email && <p>{tenant.settings.agency_email}</p>}
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm rounded-xl border-l-4 border-l-[#DC3545] lg:col-span-2" data-testid="action-required-panel">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-[#DC3545]" /> Perlu Tindakan — Tempahan Terkini Menunggu Kelulusan
                </CardTitle>
                <CardDescription className="text-xs mt-0.5">
                  <span className="font-bold text-[#DC3545]">{data.pending_bookings?.length || 0}</span> permohonan terkini menunggu tindakan anda.
                </CardDescription>
              </div>
              <Link to="/tempahan?status=menunggu">
                <Button size="sm" className="bg-[#DC3545] hover:bg-[#bb2d3b] h-8" data-testid="view-all-pending-btn">
                  Lihat Semua <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {(!data.pending_bookings || data.pending_bookings.length === 0) ? (
              <p className="text-sm text-muted-foreground py-2">Tiada tempahan menunggu. Semua telah diproses.</p>
            ) : (
              <div className="divide-y">
                {[...data.pending_bookings]
                  .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
                  .slice(0, 5)
                  .map((b) => (
                  <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 px-2 -mx-2 rounded-md hover:bg-red-50/50 transition-colors" data-testid={`action-booking-${b.id}`}>
                    <div className="flex items-start gap-2.5 min-w-0 flex-1">
                      <span className="h-8 w-8 rounded-full bg-red-50 border border-red-200 flex items-center justify-center shrink-0">
                        <Clock className="h-4 w-4 text-[#DC3545]" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold flex items-center gap-1.5 min-w-0">
                          <span className="shrink-0">{b.driver_name}</span> <span className="text-slate-400 shrink-0">→</span>
                          <span className="inline-flex items-center gap-1 text-[#10056E] min-w-0">
                            <MapPin className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{b.destination}</span>
                          </span>
                        </p>
                        <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                          <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {fmtDateTime(b.start_datetime)}</span>
                          <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {b.passengers || 1} penumpang</span>
                          <span className="inline-flex items-center gap-1"><Route className="h-3 w-3" /> {b.estimated_distance} km</span>
                          <span className="inline-flex items-center rounded-full bg-red-50 text-[#DC3545] border border-red-200 px-1.5 py-px text-[10px] font-semibold">
                            dimohon {fmtDate(b.created_at)}
                          </span>
                        </p>
                      </div>
                    </div>
                    <Link to={`/tempahan/${b.id}`}>
                      <Button size="sm" variant="outline" className="h-8 text-xs border-[#DC3545] text-[#DC3545] hover:bg-red-50" data-testid={`process-booking-${b.id}`}>
                        Proses <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      )}

      {/* Stat cards */}
      {isDriver ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <StatCard icon={Clock} label="Tempahan Menunggu" value={s.menunggu} testId="stat-menunggu" tone="amber" />
          <StatCard icon={CheckCircle2} label="Tempahan Diluluskan" value={s.diluluskan} testId="stat-diluluskan" tone="green" />
          <StatCard icon={Route} label="Jumlah Perjalanan" value={s.jumlah_trip} testId="stat-trips" tone="indigo" />
          <StatCard icon={Gauge} label="Jumlah Jarak" value={`${s.jumlah_jarak} km`} testId="stat-jarak" tone="blue" />
          <StatCard icon={Fuel} label="Purata Km/L" value={s.purata_kmpl} testId="stat-kmpl" tone="teal" />
          <StatCard icon={Droplets} label="Jumlah Bahan Api" value={`${s.jumlah_liter ?? 0} L`} testId="stat-liters" tone="purple" />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Car} label="Jumlah Kenderaan" value={s.jumlah_kenderaan} sub={`${s.kenderaan_tersedia} tersedia`} testId="stat-vehicles" tone="indigo" />
          <StatCard icon={Clock} label="Tempahan Menunggu" value={s.tempahan_menunggu} sub="menunggu kelulusan" testId="stat-pending" tone="amber" />
          <StatCard icon={CheckCircle2} label="Tempahan Aktif" value={s.tempahan_aktif} sub="telah diluluskan" testId="stat-active" tone="green" />
          <StatCard icon={Route} label="Trip Hari Ini" value={s.trip_hari_ini} sub="sedang berjalan" testId="stat-today" tone="blue" />
          <StatCard icon={Gauge} label="Penggunaan Fleet" value={`${s.penggunaan_fleet}%`} sub="kadar penggunaan hari ini" testId="stat-utilization" tone="purple" />
          <StatCard icon={Fuel} label="Purata Km/L" value={s.purata_kmpl} sub="keseluruhan fleet" testId="stat-kmpl" tone="teal" />
          <StatCard icon={Wrench} label="Dalam Penyelenggaraan" value={s.dalam_penyelenggaraan ?? 0} sub="kenderaan di bengkel" testId="stat-maintenance" tone="red" />
          <StatCard icon={CalendarCheck} label="Kenderaan Tersedia" value={s.kenderaan_tersedia} sub="sedia untuk tugasan" testId="stat-available" tone="pink" />
        </div>
      )}

      {/* Utilization fairness: most used vehicles & pemandu */}
      {!isDriver && data.utilization_30d && (
        <div className="space-y-4" data-testid="utilization-section">
          <div>
            <h2 className="text-base font-bold tracking-tight">Penggunaan Fleet & Pemandu — 30 Hari Terakhir</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Pantau keseimbangan agihan tugasan supaya kenderaan & pemandu yang sama tidak digunakan berulang kali.</p>
          </div>
          <div className="flex flex-wrap gap-3" data-testid="utilization-chips">
            <UtilChip label="Jumlah Tugasan" value={data.utilization_30d.total_assignments} sub={`${data.utilization_30d.total_km} km dianggarkan`} />
            <UtilChip label="Kenderaan Digunakan" value={`${data.utilization_30d.vehicles_used}/${data.utilization_30d.vehicles_total}`}
              sub={`${data.utilization_30d.vehicles_total ? Math.round((data.utilization_30d.vehicles_used / data.utilization_30d.vehicles_total) * 100) : 0}% daripada fleet`} />
            <UtilChip label="Pemandu Digunakan" value={`${data.utilization_30d.pemandu_used}/${data.utilization_30d.pemandu_total}`}
              sub={`${data.utilization_30d.idle_pemandu_count} belum ditugaskan`} />
            <UtilChip label="Keseimbangan Kenderaan" value={`${data.utilization_30d.balance_vehicle}%`}
              sub="purata ÷ tertinggi" tone={data.utilization_30d.balance_vehicle < 50 ? "text-[#DC3545]" : "text-[#198754]"} />
            <UtilChip label="Keseimbangan Pemandu" value={`${data.utilization_30d.balance_pemandu}%`}
              sub="purata ÷ tertinggi" tone={data.utilization_30d.balance_pemandu < 50 ? "text-[#DC3545]" : "text-[#198754]"} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <UtilChart title="Utilisasi Kenderaan" desc="Top 8 · bilangan tugasan & jarak"
              items={data.utilization_30d.vehicles} avg={data.utilization_30d.avg_vehicle}
              idleText={`${data.utilization_30d.idle_vehicles_count} kenderaan tidak digunakan langsung dalam 30 hari:`}
              idleBadges={data.utilization_30d.idle_vehicles} testId="util-vehicles-chart" />
            <UtilChart title="Utilisasi Pemandu" desc="Top 8 · bilangan tugasan & jarak"
              items={data.utilization_30d.pemandu} avg={data.utilization_30d.avg_pemandu}
              idleText={`${data.utilization_30d.idle_pemandu_count} pemandu belum menerima sebarang tugasan dalam 30 hari — sistem akan mengutamakan mereka semasa cadangan.`}
              testId="util-pemandu-chart" />
          </div>
        </div>
      )}

      {/* Telematics map */}
      {!isDriver && (
        <Card className="border-slate-200 shadow-sm rounded-xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-semibold">Lokasi Kenderaan (Telematik)</CardTitle>
            <CardDescription className="text-xs">Klik penanda atau cari kenderaan untuk maklumat lanjut.</CardDescription>
          </CardHeader>
          <CardContent>
            <FleetMap positions={positions} />
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className={`grid grid-cols-1 gap-6 ${isDriver ? "lg:grid-cols-2" : "lg:grid-cols-3"}`}>
        <Card className="border-slate-200 shadow-sm rounded-xl">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Trend Km/Liter Fleet</CardTitle>
            <CardDescription className="text-xs">Kadar Km/Liter mengikut perjalanan terkini (WP 4.1)</CardDescription>
          </CardHeader>
          <CardContent>
            {data.kmpl_trend?.length ? (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.kmpl_trend.map((d) => ({ ...d, label: fmtDate(d.label) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="kmpl" stroke="#10056E" strokeWidth={2.5} dot={{ r: 3, fill: "#10056E" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground py-12 text-center">Tiada data perjalanan lagi.</p>
            )}
          </CardContent>
        </Card>

        {isDriver ? (
          <Card className="border-slate-200 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-semibold">Perjalanan Terkini</CardTitle>
              <CardDescription className="text-xs">5 perjalanan terakhir anda</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.recent_trips?.length === 0 && <p className="text-sm text-muted-foreground">Tiada perjalanan lagi.</p>}
              {data.recent_trips?.map((t) => (
                <div key={t.id} className="flex items-center justify-between border-b last:border-0 pb-3 last:pb-0">
                  <div>
                    <p className="text-sm font-semibold">{t.destination}</p>
                    <p className="text-xs text-muted-foreground">{t.vehicle_reg} · {t.distance_km} km</p>
                  </div>
                  <Badge className="bg-[#10056E] hover:bg-[#10056E]">
                    {t.kmpl ?? "-"} Km/L
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="border-slate-200 shadow-sm rounded-xl">
              <CardHeader>
                <CardTitle className="text-base font-semibold">Penggunaan Kenderaan</CardTitle>
                <CardDescription className="text-xs">Jumlah jarak (KM) mengikut kenderaan</CardDescription>
              </CardHeader>
              <CardContent>
                {data.vehicle_usage?.length ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data.vehicle_usage}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="kenderaan" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="jarak" fill="#10056E" radius={[4, 4, 0, 0]} barSize={36} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-muted-foreground py-12 text-center">Tiada data lagi.</p>
                )}
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-sm rounded-xl">
              <CardHeader>
                <CardTitle className="text-base font-semibold">Status Kenderaan</CardTitle>
                <CardDescription className="text-xs">Pecahan fleet mengikut status</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.status_breakdown} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="status" width={110} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="bilangan" fill="#4F46E5" radius={[0, 4, 4, 0]} barSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </>
        )}
      </div>

    </div>
  );
}
